import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Modal, Spinner } from "flowbite-react";
import EventApi, { ContactSessionBoothDetail, ContactSessionMap } from "../../Api/EventApi";
import { parseBoothRegion } from "../../Models/BoothRegion";

export default function ContactSessionMapPage() {
  const { id } = useParams();
  const [map, setMap] = useState<ContactSessionMap | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ContactSessionBoothDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const eventId = Number(id);
    if (!eventId) return;
    EventApi.getContactSessionMap(eventId)
      .then((res) => {
        setMap(res.data);
        setMissing(false);
      })
      .catch(() => {
        setMap(null);
        setMissing(true);
      });
  }, [id]);

  useEffect(() => {
    const eventId = Number(id);
    if (!eventId || selectedId == null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    EventApi.getContactSessionBooth(eventId, selectedId)
      .then((res) => {
        if (!cancelled) setDetail(res.data);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, selectedId]);

  const selected = map?.booths.find((b) => b.id === selectedId) ?? null;
  const location = [detail?.city, detail?.state].filter(Boolean).join(", ");
  const website = detail?.website
    ? detail.website.startsWith("http://") || detail.website.startsWith("https://")
      ? detail.website
      : `https://${detail.website}`
    : null;

  if (missing) {
    return <div className="p-6 text-gray-600">Contact session map not found.</div>;
  }
  if (!map) {
    return <div className="p-6 text-gray-600">Loading map…</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900">{map.eventName || "Contact Session"}</h1>
      <p className="text-sm text-gray-600 mt-1 mb-4">
        Click a company on the map.
      </p>
      {!map.floorplanUrl ? (
        <p className="text-gray-600">No floorplan has been published for this event.</p>
      ) : (
        <div className="relative inline-block max-w-full border rounded bg-white">
          <img src={map.floorplanUrl} alt="Contact session floorplan" className="block max-w-full h-auto" />
          {map.booths.map((booth) => {
            const region = parseBoothRegion(booth.regionJson ?? undefined);
            if (!region || booth.id == null) return null;
            const [left, top, width, height] = region.coords;
            return (
              <button
                key={booth.id}
                type="button"
                title={booth.displayName}
                className="absolute border-2 border-blue-700 bg-blue-500/20 hover:bg-blue-500/40"
                style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}
                onClick={() => {
                  setDetail(null);
                  setDetailLoading(true);
                  setSelectedId(booth.id!);
                }}
              />
            );
          })}
        </div>
      )}

      <Modal show={selected != null} onClose={() => setSelectedId(null)} size="lg">
        <Modal.Header>{detail?.displayName || selected?.displayName || "Company"}</Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="flex items-center gap-3 text-gray-600">
              <Spinner size="md" />
              Loading company…
            </div>
          ) : !detail?.memberActive ? (
            <p className="text-sm text-gray-700">
              {detail?.displayName || selected?.displayName || "This company"} is not currently an EMDA member.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-4">
                {detail.logoUrl && (
                  <img src={detail.logoUrl} alt="" className="h-16 w-16 object-contain border rounded bg-white" />
                )}
                <div className="min-w-0">
                  {detail.headline && detail.headline.trim().toLowerCase() !== detail.displayName.trim().toLowerCase() && (
                    <p className="text-sm text-gray-700">{detail.headline}</p>
                  )}
                  {location && <p className="text-sm text-gray-600">{location}</p>}
                  {detail.phone && <p className="text-sm text-gray-600">{detail.phone}</p>}
                  {website && (
                    <a href={website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                      {detail.website}
                    </a>
                  )}
                  <Link
                    className="block mt-1 text-sm text-blue-600 hover:underline"
                    to={`/member/${encodeURIComponent(detail.displayName)}/${detail.memberId}`}
                  >
                    View member profile
                  </Link>
                </div>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  {detail.conferenceName ? `${detail.conferenceName} attendees` : "Conference attendees"}
                </h2>
                <p className="text-xs text-gray-500 mb-2">Member admins and users registered for this conference.</p>
                {detail.participants.length === 0 ? (
                  <p className="text-sm text-gray-500">None registered.</p>
                ) : (
                  <ul className="divide-y border rounded">
                    {detail.participants.map((person, index) => (
                      <li key={`${person.firstName}-${person.lastName}-${index}`} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span>{[person.firstName, person.lastName].filter(Boolean).join(" ") || "Unnamed"}</span>
                        <span className="text-xs font-medium text-gray-600">{person.role}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
